import {
  actions as xstateActions,
  AnyEventObject,
  assign,
  createMachine,
  DoneInvokeEvent,
} from 'xstate';

import { CustomObject, EnrichmentAttribute } from '~/generated/graphql';

import * as guards from './guards';
import { ObjectConfigurationContext } from './types';
import { CustomObjectsClient } from './useCustomObjects';

type ObjectSelectEvent = {
  type: 'SELECT_OBJECT';
  object: CustomObject;
};

type ObjectRemoveEvent = {
  type: 'REMOVE_OBJECT';
  object: CustomObject;
};

type EmailAttributeSelectEvent = {
  type: 'SET_EMAIL_ATTRIBUTE';
  attribute: EnrichmentAttribute;
};

type DomainAttributeSelectEvent = {
  type: 'SET_DOMAIN_ATTRIBUTE';
  attribute: EnrichmentAttribute;
};

type InitEvent = {
  type: 'INIT';
  client: CustomObjectsClient;
};

type ObjectConfigurationEvent =
  | ObjectSelectEvent
  | EmailAttributeSelectEvent
  | DomainAttributeSelectEvent
  | InitEvent
  | { type: 'SAVE' }
  | { type: 'RETRY' }
  | { type: 'LOAD' }
  | AnyEventObject;

const { send } = xstateActions;

const retryStrategy = (delay: number) => ({
  states: {
    waiting: {
      on: {
        RETRY: {
          target: 'retries',
          cond: 'hasMoreRetries',
        },
      },
    },
    retries: {
      after: {
        getDelay: {
          target: 'increment',
          delay: (
            context: ObjectConfigurationContext,
            _event: AnyEventObject,
          ) => Math.pow(2, context.retries!) * delay,
        },
      },
    },
    increment: {
      entry: 'incrementRetry',
      on: {
        always: 'waiting',
      },
    },
    done: {
      type: 'final',
    },
  },
});

const objectConfigurationMachine = {
  id: 'objectConfiguration',
  initial: 'initializing',
  predictableActionArguments: true,
  schema: {
    context: {} as ObjectConfigurationContext,
    events: {} as ObjectConfigurationEvent,
  },
  context: {
    objects: [],
    enabledObjects: [],
    disabledObjects: [],
    targetAttributes: [],
    selectedObject: null,
    selectedEmailAttribute: null,
    selectedDomainAttribute: null,
    retries: 0,
    maxRetries: 3,
    error: null,
  },
  states: {
    initializing: {
      on: {
        INIT: {
          target: 'loading',
          actions: assign({
            client: (context: ObjectConfigurationContext, event: InitEvent) =>
              event.client ?? context.client,
          }),
        },
      },
    },
    idle: {
      on: {
        LOAD: 'loading',
      },
    },
    loading: {
      initial: 'waiting',
      invoke: {
        id: 'fetchObjects',
        src: 'fetchObjectsService',
        onDone: {
          target: 'loaded',
          actions: assign({
            objects: (_, event: DoneInvokeEvent<any>) => {
              return event.data.objects;
            },
            enabledObjects: (_, event: DoneInvokeEvent<any>) => {
              return event.data.objects.filter(
                (obj: any) => obj.disabled === false,
              );
            },
            disabledObjects: (_, event: DoneInvokeEvent<any>) => {
              return event.data.objects.filter(
                (obj: any) => obj.disabled === true,
              );
            },
            targetAttributes: (_, event: DoneInvokeEvent<any>) => {
              return event.data.targetAttributes;
            },
          }),
        },
        onError: {
          target: 'loadError',
        },
      },
      ...retryStrategy(1000),
    },
    loaded: {
      on: {
        RETRY: 'loading',
        BEGIN_MANAGING: {
          target: 'managing',
          actions: 'setFormVisibility',
        },
        BEGIN_CONFIGURING: {
          target: 'configuring',
          actions: assign({
            selectedObject: (
              _context: ObjectConfigurationContext,
              event: ObjectSelectEvent,
            ) => event.object,
          }),
        },
      },
    },
    managing: {
      entry: [
        'setFormVisibility',
        assign({
          isEmptyState: (context: ObjectConfigurationContext) => {
            return context.enabledObjects?.length === 0;
          },
        }),
      ],
      on: {
        BEGIN_LINKING: {
          actions: assign({
            isShowingLinkForm: true,
          }),
        },
        STOP_LINKING: {
          actions: ['resetForm', 'setFormVisibility'],
        },
        SELECT_EMAIL_ATTRIBUTE: {
          actions: assign({
            selectedEmailAttribute: (_, event: DomainAttributeSelectEvent) =>
              event.attribute,
          }),
        },
        SELECT_DOMAIN_ATTRIBUTE: {
          actions: assign({
            selectedDomainAttribute: (_, event: DomainAttributeSelectEvent) =>
              event.attribute,
          }),
        },
        SELECT_OBJECT: {
          target: 'loadingAttributes',
          actions: assign({
            selectedObject: (_, event: ObjectSelectEvent) => event.object,
          }),
        },
        SAVE: {
          target: 'saving',
          guards: 'canSelectAttribute',
        },
        CANCEL: {
          target: 'loaded',
          actions: ['resetForm', 'setFormVisibility'],
        },
        REMOVE_OBJECT: {
          // TODO: Wire to GraphQL, make a state removing state like saving
          actions: assign({
            enabledObjects: (
              context: ObjectConfigurationContext,
              event: ObjectRemoveEvent,
            ) => {
              return context.enabledObjects!.filter(
                (obj: any) => obj.id !== event.object.id,
              );
            },
            disabledObjects: (
              context: ObjectConfigurationContext,
              event: ObjectRemoveEvent,
            ) => {
              return context.disabledObjects!.concat(event.object);
            },
          }),
        },
      },
    },
    loadingAttributes: {
      initial: 'waiting',
      invoke: {
        id: 'fetchAttributes',
        src: 'fetchAttributesService',
        onDone: {
          target: 'managing',
          actions: assign({
            targetAttributes: (_, event: DoneInvokeEvent<any>) => {
              return event.data.targetAttributes;
            },
          }),
        },
        onError: {
          target: 'loadError',
        },
      },
      ...retryStrategy(1000),
    },
    loadError: {
      on: {
        RETRY: 'loading',
      },
      ...retryStrategy(1000),
    },
    saving: {
      invoke: {
        id: 'saveObject',
        src: 'saveObjectService',
        onDone: 'loaded',
        onError: 'saveError',
      },
      ...retryStrategy(1000),
    },
    configuring: {
      on: {
        BEGIN_MANAGING: 'managing',
      },
    },
    saveError: {
      on: {
        RETRY: {
          actions: send('RETRY', { to: 'saving' }),
        },
      },
      ...retryStrategy(1000),
    },
  },
};

const actions = {
  incrementRetry: assign({
    retries: (context: ObjectConfigurationContext, _event: AnyEventObject) =>
      context.retries! + 1,
  }),
  setFormVisibility: assign({
    isShowingLinkForm: (context: ObjectConfigurationContext) => {
      return context.enabledObjects?.length === 0;
    },
  }),
  resetForm: assign({
    selectedObject: null,
    selectedEmailAttribute: null,
    selectedDomainAttribute: null,
    targetAttributes: [],
  }),
};

const services = {
  fetchObjectsService: async (
    context: ObjectConfigurationContext,
    _event: ObjectConfigurationEvent,
  ) => {
    const {
      data: { customObjects },
    } = await context.client!.fetch();

    return {
      objects: customObjects?.nodes || [],
    };
  },
  fetchAttributesService: async (context: ObjectConfigurationContext) => {
    const {
      data: { targetAttributes },
      // TODO: Pass selected object here so we can derive the query variables
    } = await context.client!.fetchTargetAttributes();

    return {
      targetAttributes: targetAttributes || [],
    };
  },
  saveObjectService: (
    _context: ObjectConfigurationContext,
    _event: ObjectConfigurationEvent,
  ) => {
    // TODO: Wire into gql
  },
};

export function createCustomObjectConfigurationMachine(
  context: ObjectConfigurationContext,
) {
  // @ts-expect-error TODO: FIX ME
  return createMachine(objectConfigurationMachine, {
    ...context,
    actions,
    guards,
    services,
  });
}
